import { get } from 'lodash/fp';
import moment from 'moment';
import type * as React from 'react';
import { DeprecatedButtonContainer } from '../../react/components/ButtonContainer';
import { Card, CardHeader, CardHeaderHeading, CardSection } from '../../react/components/Cards';
import style from './style.m.less';

type RouteErrorContent = {
    title: React.ReactNode;
    description: React.ReactNode;
    background?: string;
};

type Props = {
    errorCode?: string | number | null;
    error?: unknown;
    renderInfoButton?: (errorCode: keyof typeof contentByCode) => React.ReactNode;
};

const contentByError: { check: (error: Error) => boolean; content: RouteErrorContent }[] = [
    {
        check: error => get('name', error) === 'ChunkLoadError',
        content: {
            title: 'Loading Error',
            description: (
                <>
                    The application code required for this page could not be loaded. Please reload the page.
                    <br />
                    If the issue persists, clear the browser cache, use private mode, or try a different browser.
                </>
            ),
            background: style.internalError,
        },
    },
];

const contentByCode = {
    '-1': {
        title: 'There is no Internet Connection',
        description:
            "Please ensure that your internet connection is working. Due to connectivity issues we couldn't load:",
        background: undefined,
    },
    401: {
        title: 'Not Authorized',
        description: 'Maybe you need to login again to gain access to the following page:',
        background: undefined,
    },
    403: {
        title: 'Access Denied',
        description: `You don't have permission to access this page. If you've recently been granted Admin permissions,
            please log out then back in to refresh your permissions.
            If that doesn't work, please reach out to your existing administrators.`,
        background: style.accessDenied,
    },
    404: {
        title: 'Not Found',
        description: 'Sorry, the page you requested was not found.',
        background: style.notFound,
    },
    500: {
        title: 'Internal Error',
        description: 'Something went wrong internally while accessing the following page:',
        background: style.internalError,
    },
};

const getErrorCode = (errorCode: string | number | undefined | null): keyof typeof contentByCode => {
    if (errorCode && errorCode in contentByCode) {
        return errorCode as keyof typeof contentByCode;
    }
    return 500;
};

const getError = ({ error, errorCode }: Props): { content: RouteErrorContent; code: keyof typeof contentByCode } => {
    if (error instanceof Error) {
        const codeFromError: unknown = get('response.status', error);
        const code = getErrorCode(
            typeof codeFromError === 'string' || typeof codeFromError === 'number' ? codeFromError : errorCode
        );
        const content = contentByError.find(({ check }) => check(error))?.content || contentByCode[code];
        return { code, content };
    } else {
        const code = getErrorCode(errorCode);
        const content = contentByCode[code];
        return { code, content };
    }
};

/**
 * Make sure not to import/use *any* references to the currentUser
 * in this component – it's used before that is available.
 */
export function RouteErrorPlain(props: Props) {
    const { content } = getError(props);
    return (
        <div className={content.background}>
            <ErrorCard {...props} />
        </div>
    );
}

export function ErrorCard(props: Props) {
    const { content, code } = getError(props);
    return (
        <Card className={style.card}>
            <ErrorDescription {...content} />
            <CardSection>
                <PageUrl />
                <DeprecatedButtonContainer>
                    {props.renderInfoButton?.(code)}
                    <a className="button save" href="/app/home">
                        Return Home
                    </a>
                </DeprecatedButtonContainer>
            </CardSection>
        </Card>
    );
}

function ErrorDescription({ title, description }: RouteErrorContent) {
    return (
        <div data-test="route-error-description">
            <CardHeader>
                <CardHeaderHeading>{title}</CardHeaderHeading>
            </CardHeader>
            <CardSection>
                <p>{description}</p>
            </CardSection>
        </div>
    );
}

function PageUrl() {
    const { href } = window.location;
    const date = moment.utc().format();
    return (
        <code className="clearfix">
            <span style={{ wordWrap: 'break-word', display: 'block' }}>{href}</span>
            <br></br>
            <span style={{ color: '#BABFC2' }}>
                {date} | {__currentRev__}
            </span>
        </code>
    );
}
